/**
 * The bundled Arabic voice.
 *
 * Every Arabic phrase the app can say was recorded ahead of time by
 * `npm run audio` and ships with the app as a small mp3. This engine simply
 * plays the right file.
 *
 * It exists because the browser's own speech only works if the operating system
 * has an Arabic voice installed, and most Windows PCs — and plenty of phones —
 * do not. Rather than go silent (and never, ever read Arabic in an English
 * voice), the app carries its own.
 *
 * Only Arabic is recorded. Every device already has an English voice, so
 * English still goes through the browser.
 */
import type { SpeakOptions, SpeechEngine, TtsVoice } from './types';
import { clipKey } from './clipKey';

/** Written by tools/audio/render.py alongside the clips. */
interface ClipIndex {
  voice: string;
  format: string;
  count: number;
  totalBytes: number;
  totalMs: number;
  /** key -> [durationMs, bytes] */
  clips: Record<string, [number, number]>;
}

export const BUNDLED_VOICE_ID = 'bundled-ar';

/**
 * Clips are fetched rather than handed to <audio src>, so the service worker
 * sees an ordinary request it can cache. Setting src directly makes the browser
 * send a Range request, which comes back as a 206 that the Cache API refuses to
 * store — the app would then need the network every time.
 */
export class ClipEngine implements SpeechEngine {
  readonly id = 'bundled' as const;
  readonly displayNameEn = 'Built-in Arabic voice';
  readonly displayNameAr = 'الصوت العربي المرفق';
  readonly supportsPause = true;

  private index: ClipIndex | null = null;
  private listeners = new Set<() => void>();
  /** key -> blob URL. Clips are tiny and get replayed a lot. */
  private cache = new Map<string, string>();
  private baseUrl: string;
  lastError: string | null = null;

  /*
   * One element, reused for every clip.
   *
   * iPhones and iPads refuse to play audio that was not started by a tap. Once
   * a single element has played inside a real tap it stays playable, so the app
   * silently "unlocks" this one on the first touch and then reuses it — which
   * is what lets the question read itself aloud when it appears.
   */
  private el: HTMLAudioElement | null = null;
  private unlocked = false;

  constructor(baseUrl = 'audio/ar') {
    // Relative to <base>, so the app still works if it is ever served from a
    // sub-folder rather than the root of a domain.
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    if (typeof window !== 'undefined') {
      const unlock = () => this.unlock();
      for (const event of ['pointerdown', 'touchend', 'keydown'] as const) {
        window.addEventListener(event, unlock, { once: false, passive: true });
      }
    }
  }

  private element(): HTMLAudioElement {
    if (!this.el) {
      this.el = new Audio();
      this.el.preload = 'auto';
    }
    return this.el;
  }

  /** Called from a real user gesture. Cheap, and harmless to repeat. */
  private unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    const el = this.element();
    if (!el.src) {
      // 0.05 s of silence — just enough to count as "this element has played".
      el.src =
        'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//WreyTRUoAWgBgkOAGbZHBgG1OF6zM82DWbZaUmMBptgQhGjsyYqc9ae9XFz280948NMBWInljyzsNu0n6i1//9//pauRUEA==';
      void el.play().then(() => el.pause()).catch(() => undefined);
    }
  }

  async init(): Promise<void> {
    if (this.index) return;
    try {
      const res = await fetch(`${this.baseUrl}/index.json`);
      if (!res.ok) throw new Error(`index.json answered ${res.status}`);
      const data = (await res.json()) as ClipIndex;
      if (!data || typeof data !== 'object' || !data.clips) throw new Error('index.json is malformed');
      this.index = data;
      this.lastError = null;
    } catch (err) {
      this.index = null;
      this.lastError = err instanceof Error ? err.message : 'Bundled audio not found';
    }
    this.listeners.forEach((l) => l());
  }

  /** True once the recordings are known to exist. */
  get ready(): boolean {
    return this.index !== null && this.index.count > 0;
  }

  get stats(): { count: number; totalBytes: number; totalMs: number } | null {
    if (!this.index) return null;
    const { count, totalBytes, totalMs } = this.index;
    return { count, totalBytes, totalMs };
  }

  getVoices(): TtsVoice[] {
    if (!this.ready) return [];
    return [
      {
        id: BUNDLED_VOICE_ID,
        name: this.displayNameAr,
        lang: 'ar-JO',
        isArabic: true,
        isEnglish: false,
        isDefault: true,
      },
    ];
  }

  onVoicesChanged(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Whether this exact phrase was recorded. */
  has(text: string): boolean {
    if (!this.index) return false;
    return Object.prototype.hasOwnProperty.call(this.index.clips, clipKey(text));
  }

  /** Every clip URL, for the "make it work offline" download in Settings. */
  allUrls(): string[] {
    if (!this.index) return [];
    return Object.keys(this.index.clips).map((key) => `${this.baseUrl}/${key}.mp3`);
  }

  private async urlFor(key: string, signal: AbortSignal): Promise<string | null> {
    const cached = this.cache.get(key);
    if (cached) return cached;

    const res = await fetch(`${this.baseUrl}/${key}.mp3`, { signal });
    if (!res.ok) throw new Error(`clip ${key} answered ${res.status}`);
    const url = URL.createObjectURL(await res.blob());

    // Keep the working set small; a quiz only ever revisits a few phrases.
    if (this.cache.size >= 60) {
      const oldest = this.cache.keys().next().value;
      if (oldest !== undefined) {
        URL.revokeObjectURL(this.cache.get(oldest) as string);
        this.cache.delete(oldest);
      }
    }
    this.cache.set(key, url);
    return url;
  }

  async speak(text: string, options: SpeakOptions): Promise<void> {
    if (options.signal.aborted) return;
    if (options.lang !== 'ar') return; // English is the browser's job
    const key = clipKey(text);
    if (!this.index || !this.index.clips[key]) return;

    try {
      const url = await this.urlFor(key, options.signal);
      if (!url || options.signal.aborted) return;

      const audio = this.element();
      audio.src = url;
      // The clips are recorded at a natural pace; the app's speed setting slows
      // them down here. Keeping the pitch steady is what stops a slowed voice
      // from sounding like a cartoon.
      audio.preservesPitch = true;
      audio.playbackRate = clampRate(options.rate);
      audio.currentTime = 0;
      this.lastError = null;

      await new Promise<void>((resolve) => {
        const done = () => {
          // The element is reused, so every handler has to be taken off again
          // or the next clip would finish the previous clip's promise.
          audio.onended = null;
          audio.onerror = null;
          options.signal.removeEventListener('abort', onAbort);
          resolve();
        };
        const onAbort = () => {
          audio.pause();
          done();
        };
        audio.onended = done;
        audio.onerror = done;
        options.signal.addEventListener('abort', onAbort);
        void audio.play().catch(done);
      });
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        this.lastError = err instanceof Error ? err.message : 'Could not play the bundled audio';
        this.listeners.forEach((l) => l());
      }
    }
  }

  pause(): void {
    this.el?.pause();
  }

  resume(): void {
    void this.el?.play().catch(() => undefined);
  }

  cancel(): void {
    if (this.el) this.el.pause();
  }
}

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  // Below about 0.5 even pitch-corrected speech starts to smear.
  return Math.min(2, Math.max(0.5, rate));
}
